import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDay } from "@/components/letters/LetterCards";
import { HelpedQuoteButton } from "@/components/playbook/HelpedQuoteButton";

const TINTS = ["#FCE8EE", "#EEE8FA", "#E6F3FB", "#FFF3C9", "#E7F6EC", "#FDEBDD"];
const tint = (topic) => TINTS[[...topic].reduce((a, c) => a + c.charCodeAt(0), 0) % TINTS.length];
const opened = (n) => (n ? `Opened ${n} time${n === 1 ? "" : "s"}` : "Not opened yet");

const FRIEND = { background: "linear-gradient(135deg,#FFE3EC,#FFF4D6)" };

export const OpenWhenEnvelope = ({ l, onOpen }) => (
  <button onClick={() => onOpen(l)} className={`relative rounded-3xl p-4 text-left text-[#3A2E4F] hover:-translate-y-1 hover:rotate-[-1deg] transition-transform shadow-sm ${l.from_name ? "border-2 border-[#F28FAD]/60" : ""}`}
    style={l.from_name ? FRIEND : { background: tint(l.topic) }} data-testid={`open-when-${l.id}`}>
    {l.from_name && <span className="absolute -top-2 right-3 rounded-full bg-[#D94F7A] text-white text-[10px] font-bold px-2 py-0.5 max-w-[70%] truncate" data-testid={`open-when-from-${l.id}`}>From {l.from_name}</span>}
    <div className="text-2xl" aria-hidden>{l.from_name ? "💝" : "💌"}</div>
    <div className="text-xs uppercase tracking-wider font-semibold opacity-60 mt-2">Open when…</div>
    <div className="font-bold leading-snug break-words [overflow-wrap:anywhere]">{l.topic}</div>
    <div className="text-xs opacity-70 mt-2" data-testid={`open-when-count-${l.id}`}>{opened(l.open_count)}</div>
  </button>
);

export const OpenWhenComposer = ({ topics, onSaved, onCancel }) => {
  const [topic, setTopic] = useState(topics[0]);
  const [custom, setCustom] = useState(false);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post("/open-when", { topic: topic.trim(), body: body.trim() }); toast.success("Letter tucked away 💌 Open it whenever you need it"); onSaved(); }
    catch (e) { toast.error(errMsg(e, "Couldn't save your letter")); }
    finally { setBusy(false); }
  };
  return (
    <div className="mm-card p-5 mm-fade-up" data-testid="open-when-composer">
      <div className="flex items-center justify-between"><h2 className="font-semibold">Write an Open-When letter</h2><button onClick={onCancel} className="p-1 text-mm-muted" aria-label="Close" data-testid="open-when-cancel"><X size={16} /></button></div>
      <div className="text-sm text-mm-muted mt-1">Open when…</div>
      <div className="flex flex-wrap gap-2 mt-2">
        {topics.map((t, i) => <button key={t} onClick={() => { setTopic(t); setCustom(false); }} className={`mm-chip !text-xs ${!custom && topic === t ? "mm-chip-on" : ""}`} data-testid={`open-when-topic-${i}`}>{t}</button>)}
        <button onClick={() => { setCustom(true); setTopic(""); }} className={`mm-chip !text-xs ${custom ? "mm-chip-on" : ""}`} data-testid="open-when-topic-custom"><Plus size={12} />My own</button>
      </div>
      {custom && <input className="mm-input mt-3" maxLength={80} value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Open when… I miss home" data-testid="open-when-custom-input" />}
      <textarea className="mm-input min-h-[160px] mt-3" maxLength={2000} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Dear me, if you're reading this…" data-testid="open-when-body" />
      <div className="flex justify-between items-center mt-3">
        <span className="text-xs text-mm-muted">{body.length}/2000</span>
        <button className="mm-btn" onClick={save} disabled={busy || !topic.trim() || !body.trim()} data-testid="open-when-save">{busy && <Loader2 size={16} className="animate-spin" />}Save letter</button>
      </div>
    </div>
  );
};

export const OpenWhenView = ({ l, onClose, onDelete }) => {
  const [armed, setArmed] = useState(false);
  return (
    <article className="rounded-3xl p-6 sm:p-8 text-[#3A2E4F] mm-fade-up shadow-sm" style={l.from_name ? FRIEND : { background: tint(l.topic) }} data-testid="open-when-view">
      <div className="flex items-start justify-between gap-3">
        <div><div className="text-xs uppercase tracking-wider font-semibold opacity-60">Open when…</div><h2 className="text-xl font-bold break-words [overflow-wrap:anywhere]">{l.topic}</h2>
          {l.from_name && <div className="text-sm font-semibold text-[#B23A63] mt-1" data-testid="open-when-view-from">💝 From {l.from_name}</div>}</div>
        <button onClick={onClose} className="p-1 opacity-60" aria-label="Close" data-testid="open-when-close"><X size={18} /></button>
      </div>
      <p className="mt-5 whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-lg leading-relaxed" style={{ fontFamily: "Georgia, serif" }} data-testid="open-when-text">{l.body}</p>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-6 text-xs opacity-70">
        <HelpedQuoteButton text={l.body} source="letter" testId="open-when-helped" />
        <span data-testid="open-when-view-count">{l.from_name ? "Sent" : "Written"} {fmtDay(l.created_at)} · {opened(l.open_count)}</span>
        {armed ? (
          <span className="flex gap-3"><button onClick={() => onDelete(l.id)} className="text-mm-error font-semibold" data-testid="open-when-delete-yes">Delete letter</button><button onClick={() => setArmed(false)} data-testid="open-when-delete-no">Keep</button></span>
        ) : <button onClick={() => setArmed(true)} className="inline-flex items-center gap-1" data-testid="open-when-delete"><Trash2 size={14} />Delete</button>}
      </div>
    </article>
  );
};
